import type { GlobalOptions, Options } from 'canvas-confetti';
import confetti from 'canvas-confetti';

import { logger } from '@/lib/telemetry/logger';

const CONFETTI_Z_INDEX = 600;

const OUTCOME_HUES = [
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'blue',
  'indigo',
  'violet',
  'purple',
  'fuchsia',
  'pink',
] as const;

let cachedPalette: string[] | null = null;

// Resolve `--outcome-*-400` (authored in OKLCh) to hex via a 1×1 canvas.
// `getComputedStyle().color` returns the original color space in modern
// browsers — the value stays in its authored space — and canvas-confetti's
// `hexToRgb` strips every non-hex char from `colors`. We round-trip
// through a 2D context, which normalises any CSS color the browser
// understands into a canonical RGB pixel.
function getOutcomePalette(): string[] {
  if (cachedPalette !== null) return cachedPalette;
  if (typeof document === 'undefined') return [];

  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return [];

  const rootStyle = window.getComputedStyle(document.documentElement);

  cachedPalette = OUTCOME_HUES.flatMap((hue): string[] => {
    const value = rootStyle.getPropertyValue(`--outcome-${hue}-400`).trim();
    if (value === '') return [];

    // Reset to a known-transparent state, then attempt assignment. If
    // `value` isn't accepted (e.g. canvas lacks oklch support), the
    // fillStyle stays transparent and the resulting pixel has alpha 0.
    ctx.clearRect(0, 0, 1, 1);
    ctx.fillStyle = 'transparent';
    ctx.fillStyle = value;
    ctx.fillRect(0, 0, 1, 1);

    const pixel = ctx.getImageData(0, 0, 1, 1).data;
    const r = pixel[0];
    const g = pixel[1];
    const b = pixel[2];
    const a = pixel[3];
    if (r === undefined || g === undefined || b === undefined || a === undefined) return [];
    if (a < 255) return [];

    const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    return [hex];
  });
  return cachedPalette;
}

function isReducedMotion(): boolean {
  if (typeof window === 'undefined') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function fire(options: Options & GlobalOptions): void {
  if (isReducedMotion()) return;
  try {
    confetti({
      zIndex: CONFETTI_Z_INDEX,
      colors: getOutcomePalette(),
      ...options,
    });
  } catch (error) {
    logger.warn('confetti failed to fire', { error: String(error) });
  }
}

// Convert an element's bounding box into a normalized viewport origin for
// canvas-confetti. Returns `undefined` when the element is missing or has no
// box (hidden / unmounted) so callers can fall back to a default origin.
export function confettiOriginFromElement(
  el: HTMLElement | null,
): { x: number; y: number } | undefined {
  if (el === null || typeof window === 'undefined') return undefined;
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return undefined;
  return {
    x: (rect.left + rect.width / 2) / window.innerWidth,
    y: (rect.top + rect.height / 2) / window.innerHeight,
  };
}

export function fireTradeBurst(origin?: { x: number; y: number }): void {
  fire({
    particleCount: 80,
    spread: 70,
    startVelocity: 35,
    origin: origin ?? { x: 0.5, y: 0.7 },
  });
}

// Claiming a milestone or completing a quest fires this celebratory burst,
// optionally anchored over the button the action came from.
export function fireRewardBurst(origin?: { x: number; y: number }): void {
  fire({
    particleCount: 110,
    spread: 80,
    startVelocity: 38,
    origin: origin ?? { x: 0.5, y: 0.5 },
  });
}

export function fireMarketCreatedBurst(): void {
  fire({
    particleCount: 160,
    spread: 100,
    startVelocity: 45,
    origin: { x: 0.5, y: 0.6 },
  });
}

export function fireResolutionFireworks(): void {
  if (isReducedMotion()) return;

  const duration = 2500;
  const animationEnd = Date.now() + duration;
  const defaults: Options & GlobalOptions = {
    startVelocity: 30,
    spread: 360,
    ticks: 60,
    zIndex: CONFETTI_Z_INDEX,
    colors: getOutcomePalette(),
  };
  const rand = Math.random;

  const interval = window.setInterval(() => {
    const timeLeft = animationEnd - Date.now();
    if (timeLeft <= 0) {
      window.clearInterval(interval);
      return;
    }
    const particleCount = 50 * (timeLeft / duration);
    try {
      confetti({
        ...defaults,
        particleCount,
        origin: { x: rand() * 0.6 + 0.2, y: rand() - 0.2 },
      });
    } catch (error) {
      window.clearInterval(interval);
      logger.warn('confetti fireworks failed', { error: String(error) });
    }
  }, 250);
}
